import * as React from 'react'

export function useIsInViewport(
  ref?: React.RefObject<HTMLElement | null> | undefined,
  initial = false,
  options?: IntersectionObserverInit,
) {
  const [isIntersecting, setIsIntersecting] = React.useState(initial)
  const isMountedRef = React.useRef(true)

  // Memoize options to prevent observer recreation on every render
  const memoizedOptions = React.useMemo(() => options, [options])

  const observer = React.useMemo(() => {
    // oxlint-disable-next-line no-negated-condition
    return typeof window !== 'undefined'
      ? new IntersectionObserver(function (entries) {
          if (!isMountedRef.current) {
            return
          }

          // sometimes we have multiple entries, ordered by time so we can just take the last one
          const lastEntryIndex = entries.length - 1
          const entry = entries[lastEntryIndex]
          if (entry) {
            setIsIntersecting(entry.isIntersecting)
          }
        }, memoizedOptions)
      : null
  }, [memoizedOptions])

  React.useEffect(() => {
    if (ref?.current && observer !== null) {
      observer.observe(ref.current)
    }
  }, [observer, ref])

  React.useEffect(() => {
    isMountedRef.current = true

    return () => {
      isMountedRef.current = false
      if (observer) {
        observer.disconnect()
      }
    }
  }, [observer])

  return isIntersecting
}
